import { keyBy } from 'lodash/fp';
import { createSelector } from 'reselect';
import { LegalEntityIdentifierValue } from '@mews-commander/data-access';
import { createAction, createReducer } from '@mews/framework';
import { ApplicationState, IdentifierConfiguration } from '@mews-commander/types';
import { notEmpty } from '@mews-commander/utils';
import {
    getCompanyIdentifierConfigurations,
    getCustomerIdentifierConfigurations,
} from '@mews-commander/core/legacy-globalization';
import { getCurrentEnterpriseLegalEnvironmentCode } from '../selectors/session/enterprise';
import parentSelector from '../selector';
import prefix from '../prefix';
import { Entities } from './utils';

export const NAME = 'legalEntityIdentifiers';

const LEGAL_ENTITY_IDENTIFIERS_LOADED = prefix('LEGAL_ENTITY_IDENTIFIERS_LOADED');

export const setLegalEntityIdentifiers = (legalEntityIdentifiers: Array<LegalEntityIdentifierValue>) =>
    createAction(LEGAL_ENTITY_IDENTIFIERS_LOADED, legalEntityIdentifiers);

export const legalEntityIdentifiersReducer = createReducer(
    {},
    {
        [LEGAL_ENTITY_IDENTIFIERS_LOADED]: (state: ApplicationState, payload: Array<LegalEntityIdentifierValue>) => ({
            ...keyBy('legalEntityIdentifierCode', payload),
        }),
    }
);

const getLegalEntityIdentifiers = createSelector(parentSelector, (state) => state[NAME]);

const getLegalEntityIdentifiersCodeValue = (
    identifierConfigurations: Entities<IdentifierConfiguration>,
    {
        legalEntityIdentifiers,
        legalEnvironmentCode,
    }: { legalEntityIdentifiers: Entities<LegalEntityIdentifierValue>; legalEnvironmentCode: string }
) => {
    if (notEmpty(identifierConfigurations)) {
        const filteredIdentifierValues = Object.values(identifierConfigurations).filter(
            ({ accountingEnvironmentCode }) => accountingEnvironmentCode === legalEnvironmentCode
        );
        return filteredIdentifierValues.map(({ legalEntityIdentifierCode }) => ({
            [legalEntityIdentifierCode]: legalEntityIdentifiers?.[legalEntityIdentifierCode]?.value || null,
        }));
    }
    return [];
};

const getIdentifiersAndEnvironmentCode = createSelector(
    getLegalEntityIdentifiers,
    getCurrentEnterpriseLegalEnvironmentCode,
    (legalEntityIdentifiers: Entities<LegalEntityIdentifierValue>, legalEnvironmentCode: string) => ({
        legalEntityIdentifiers,
        legalEnvironmentCode,
    })
);

export const getCompanyLegalEntityIdentifiersCodeValue = createSelector(
    getCompanyIdentifierConfigurations,
    getIdentifiersAndEnvironmentCode,
    getLegalEntityIdentifiersCodeValue
);

export const getCustomerLegalEntityIdentifiersCodeValue = createSelector(
    getCustomerIdentifierConfigurations,
    getIdentifiersAndEnvironmentCode,
    getLegalEntityIdentifiersCodeValue
);
